// src/pages/Enseignants/PassagesGrades/utils/gradePassageUtils.js

export const GRADE_MARKER = "[PASSAGE_GRADE]";

const API_BASE = process.env.REACT_APP_API_URL || "";

export const todayISO = () => {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

export const formatDateFr = (dateValue) => {
  if (!dateValue) return "—";

  const d = new Date(dateValue);
  if (Number.isNaN(d.getTime())) return String(dateValue);

  return d.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

export const getGroupLabel = (group) =>
  group?.nom || group?.nom_groupe || group?.name || "";

export const getAdherentFullName = (adherent) =>
  `${adherent?.prenom || ""} ${adherent?.nom || ""}`.trim() || "Adhérent";

export const getTeacherFullName = (...sources) => {
  for (const source of sources) {
    if (!source) continue;

    const nestedUser = source.user || source.User || source.profile || null;

    const candidates = [
      [source.prenom, source.nom],
      [source.first_name, source.last_name],
      [source.firstname, source.lastname],
      [source.firstName, source.lastName],
      [source.given_name, source.family_name],
      [nestedUser?.prenom, nestedUser?.nom],
      [nestedUser?.first_name, nestedUser?.last_name],
    ];

    for (const parts of candidates) {
      const fullName = parts.filter(Boolean).join(" ").trim();
      if (fullName) return fullName;
    }

    if (source.name) return source.name;
    if (source.full_name) return source.full_name;
    if (source.email) return source.email;
  }

  return "Enseignant";
};

const cleanLineValue = (value) =>
  String(value || "")
    .replace(/\r?\n/g, " ")
    .replace(/\|/g, "-")
    .trim();

export const createGradePassageLine = ({
  date,
  result,
  teacher,
  comment,
}) => {
  const finalDate = date || todayISO();
  const finalResult = cleanLineValue(result || "Obtenu");
  const finalTeacher = cleanLineValue(teacher || "Enseignant");
  const finalComment = cleanLineValue(comment);

  return `${GRADE_MARKER} ${finalDate} | Résultat : ${finalResult} | Enseignant : ${finalTeacher}${
    finalComment ? ` | Commentaire : ${finalComment}` : ""
  }`;
};

export const parseGradePassage = (notes) => {
  const rawNotes = String(notes || "");
  const line = rawNotes
    .split(/\r?\n/)
    .map((item) => item.trim())
    .find((item) => item.includes(GRADE_MARKER));

  if (!line) {
    return {
      isGraded: false,
      date: "",
      result: "",
      teacher: "",
      comment: "",
      rawLine: "",
    };
  }

  const clean = line.replace(GRADE_MARKER, "").trim();
  const parts = clean.split("|").map((part) => part.trim());

  const date = parts[0] || "";
  const data = {};

  parts.slice(1).forEach((part) => {
    const [key, ...rest] = part.split(":");
    if (!key || !rest.length) return;

    const normalizedKey = key
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

    data[normalizedKey] = rest.join(":").trim();
  });

  return {
    isGraded: true,
    date,
    result: data.resultat || "",
    teacher: data.enseignant || "",
    comment: data.commentaire || "",
    rawLine: line,
  };
};

export const upsertGradePassageLine = (notes, payload) => {
  const rawNotes = String(notes || "");
  const generatedLine = createGradePassageLine(payload);

  const otherLines = rawNotes
    .split(/\r?\n/)
    .filter((line) => !line.includes(GRADE_MARKER))
    .map((line) => line.trim())
    .filter(Boolean);

  return [generatedLine, ...otherLines].join("\n").trim();
};

export const removeGradePassageLine = (notes) =>
  String(notes || "")
    .split(/\r?\n/)
    .filter((line) => !line.includes(GRADE_MARKER))
    .join("\n")
    .trim();

export const gradeResultTone = (result) => {
  const normalized = String(result || "").toLowerCase();

  if (normalized.includes("non")) return "failed";
  if (normalized.includes("obtenu")) return "success";

  return "graded";
};

export const gradeResultLabel = (gradeInfo) => {
  if (!gradeInfo?.isGraded) return "Non noté";
  return gradeInfo.result || "Noté";
};

export const getAdherentPhotoUrl = (adherent) => {
  const raw =
    adherent?.photoURL ||
    adherent?.photo_url ||
    adherent?.photo_adherent ||
    adherent?.image_url ||
    adherent?.avatar ||
    "";

  if (!raw) return null;
  if (/^https?:\/\//i.test(raw)) return raw;

  const cleanBase = API_BASE.replace(/\/$/, "");
  const cleanPath = String(raw).startsWith("/") ? raw : `/${raw}`;

  return `${cleanBase}${cleanPath}`;
};

export const getInitials = (adherent) => {
  const first = String(adherent?.prenom || "").trim().charAt(0);
  const last = String(adherent?.nom || "").trim().charAt(0);
  return `${first}${last}`.toUpperCase() || "AD";
};

export const calculateAge = (dateNaissance) => {
  if (!dateNaissance) return null;

  const birth = new Date(dateNaissance);
  if (Number.isNaN(birth.getTime())) return null;

  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const monthDiff = now.getMonth() - birth.getMonth();

  if (
    monthDiff < 0 ||
    (monthDiff === 0 && now.getDate() < birth.getDate())
  ) {
    age -= 1;
  }

  return age;
};

export const seasonNeighbors = (season) => {
  const raw = String(season || "");
  const match = raw.match(/(\d{4})\D+(\d{4})/);

  if (!match) {
    return {
      prev: raw,
      next: raw,
    };
  }

  const start = Number(match[1]);
  const end = Number(match[2]);
  const sep = raw.includes("/") ? "/" : "-";

  return {
    prev: `${start - 1}${sep}${end - 1}`,
    next: `${start + 1}${sep}${end + 1}`,
  };
};

export const sortAdherentsForGrade = (list, sortBy) => {
  const safeList = [...(list || [])];

  const byAlpha = (a, b) =>
    `${a?.nom || ""} ${a?.prenom || ""}`.localeCompare(
      `${b?.nom || ""} ${b?.prenom || ""}`,
      "fr",
      { sensitivity: "base" }
    );

  if (sortBy === "not_graded_first") {
    return safeList.sort((a, b) => {
      const A = parseGradePassage(a?.notes_enseignant).isGraded ? 1 : 0;
      const B = parseGradePassage(b?.notes_enseignant).isGraded ? 1 : 0;
      return A - B || byAlpha(a, b);
    });
  }

  if (sortBy === "graded_first") {
    return safeList.sort((a, b) => {
      const A = parseGradePassage(a?.notes_enseignant).isGraded ? 1 : 0;
      const B = parseGradePassage(b?.notes_enseignant).isGraded ? 1 : 0;
      return B - A || byAlpha(a, b);
    });
  }

  if (sortBy === "recent") {
    return safeList.sort((a, b) => {
      const A = parseGradePassage(a?.notes_enseignant);
      const B = parseGradePassage(b?.notes_enseignant);

      const dateA = A.date ? new Date(A.date).getTime() : 0;
      const dateB = B.date ? new Date(B.date).getTime() : 0;

      return dateB - dateA || byAlpha(a, b);
    });
  }

  return safeList.sort(byAlpha);
};